import * as React from 'react';
import type { ExternalContact } from '@bliks-io/contracts';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { DELETED_ENTITY_CLASS, useDeletedEntity } from '@/lib/useDeletedEntity';
import { Item, ItemMedia, ItemContent, ItemTitle, ItemDescription, cn } from '@bliks-io/ui';

interface ExternalContactSuccessorItemProps {
  contact: ExternalContact;
  variant?: 'default' | 'outline' | 'muted';
}

/**
 * ExternalContactSuccessorItem Component
 *
 * Displays an external contact successor using the v2 Item component.
 * Shows contact icon, name, and email as description.
 * Soft-deleted contacts render grayed out with a "(deleted)" suffix.
 */
export const ExternalContactSuccessorItem = React.forwardRef<
  HTMLDivElement,
  ExternalContactSuccessorItemProps
>(({ contact, variant = 'outline' }, ref) => {
  const { isDeleted, deletedSuffix } = useDeletedEntity(contact);
  return (
    <Item
      ref={ref}
      variant={variant}
      size="default"
      className={cn('pr-8', isDeleted && DELETED_ENTITY_CLASS)}
    >
      <ItemMedia variant="icon">
        <ENTITY_ICONS.externalContact className="h-4 w-4" />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{contact.name + deletedSuffix}</ItemTitle>
        {contact.email && <ItemDescription>{contact.email}</ItemDescription>}
      </ItemContent>
    </Item>
  );
});

ExternalContactSuccessorItem.displayName = 'ExternalContactSuccessorItem';
